import React, { useEffect, useRef, useState } from 'react';
import { MdCloudOff, MdSync, MdClose, MdCloudDone } from 'react-icons/md';
import { toast } from 'react-toastify';
import useSounds from '../../hooks/useSounds';
import { formatAge } from '../cache/useCachedData';
import { useConnection, ping } from './connection';
import { useOutbox, cancelPending, takeSentCount, subscribeOutbox, pendingCount } from './outbox';
import './liveStatus.css';

/**
 * LiveBadge — the navbar's answer to "am I connected to the server?"
 *
 *   ● Live        green, pulsing — the server answered the last heartbeat
 *   ● غير متصل    red — offline; the screens show saved copies and saves wait
 *   ⟳ n بانتظار   amber counter — saves waiting to go out; click for the list
 *
 * The heartbeat and the state live in ./connection; this only draws them.
 */
export function LiveBadge() {
  const { online, latency, lastOk } = useConnection();
  const { pending } = useOutbox();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const state = online ? 'live' : 'offline';
  const title = online
    ? `متصل بالخادم${latency != null ? ` — ${latency}ms` : ''}`
    : `غير متصل${lastOk ? ` — آخر اتصال ${formatAge(Date.now() - lastOk)}` : ''}`;

  return (
    <div className="lv" ref={ref}>
      <button
        type="button"
        className={`lv-badge is-${state}`}
        title={title}
        aria-label={title}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="lv-dot" aria-hidden="true" />
        <span className="lv-text">{online ? 'Live' : 'غير متصل'}</span>
        {pending.length > 0 && <span className="lv-count" title="تغييرات بانتظار الإرسال">{pending.length}</span>}
      </button>

      {open && (
        <div className="lv-pop" role="dialog" aria-label="حالة الاتصال">
          <div className={`lv-pop-head is-${state}`}>
            {online ? <MdCloudDone size={22} /> : <MdCloudOff size={22} />}
            <div>
              <strong>{online ? 'متصل بالخادم' : 'لا يوجد اتصال'}</strong>
              <span>
                {online
                  ? `زمن الاستجابة ${latency ?? '—'}ms`
                  : 'تتصفح نسخة محفوظة — التغييرات تُرسل تلقائياً عند عودة الاتصال'}
              </span>
            </div>
          </div>

          {pending.length > 0 ? (
            <ul className="lv-list">
              {pending.map((p) => (
                <li key={p.id}>
                  <MdSync className="lv-spin" size={16} />
                  <span className="lv-item-main">
                    <strong>{p.label}</strong>
                    <span>بانتظار الاتصال منذ {formatAge(Date.now() - p.since).replace('قبل ', '')}</span>
                  </span>
                  <button
                    type="button"
                    className="lv-cancel"
                    title="إلغاء هذا التغيير"
                    onClick={() => cancelPending(p.id)}
                  >
                    <MdClose size={15} />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="lv-empty">لا توجد تغييرات بانتظار الإرسال.</p>
          )}

          {!online && (
            <button type="button" className="lv-retry" onClick={() => ping()}>
              <MdSync size={15} /> حاول الاتصال الآن
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * OfflineBanner — a strip under the top bar while offline, and the
 * "back online, N saves sent" toast when the connection returns.
 * Mounted once, in Shell.
 */
export function OfflineBanner() {
  const { online, offlineSince } = useConnection();
  const { pending, snapshotAt } = useOutbox();
  // useSounds returns a new object each render; a ref keeps the effect
  // below keyed on `online` alone.
  const sounds = useRef(null);
  sounds.current = useSounds();
  const wasOffline = useRef(!online);

  // Announce the drop and the return — once each, not per request.
  useEffect(() => {
    if (!online && !wasOffline.current) {
      wasOffline.current = true;
      toast.warn('انقطع الاتصال — يمكنك متابعة العمل، وسيُحفظ كل شيء عند عودة الاتصال', { toastId: 'offline' });
      sounds.current.error();
    }
    if (online && wasOffline.current) {
      wasOffline.current = false;
      toast.dismiss('offline');
      // The waiting saves go out in the next moments; report when done.
      const report = () => {
        if (pendingCount() > 0) return false;
        const n = takeSentCount();
        toast.success(n ? `عاد الاتصال ✓ تم إرسال ${n} من التغييرات المعلّقة` : 'عاد الاتصال ✓', { toastId: 'online' });
        sounds.current.success();
        return true;
      };
      if (!report()) {
        const off = subscribeOutbox(() => { if (report()) off(); });
        return off;
      }
    }
    return undefined;
  }, [online]);

  if (online) return null;
  return (
    <div className="lv-banner" role="status">
      <MdCloudOff size={18} />
      <span>
        <strong>لا يوجد اتصال بالإنترنت.</strong>{' '}
        {snapshotAt
          ? `تتصفح آخر نسخة محفوظة (${formatAge(Date.now() - snapshotAt)}).`
          : 'تتصفح البيانات المحمّلة مسبقاً.'}{' '}
        {pending.length
          ? `${pending.length} ${pending.length === 1 ? 'تغيير ينتظر' : 'تغييرات تنتظر'} الإرسال — لا تغلق الصفحة.`
          : 'أي تغيير تحفظه سيُرسل تلقائياً عند عودة الاتصال.'}
      </span>
      {offlineSince && <span className="lv-banner-since">منذ {formatAge(Date.now() - offlineSince).replace('قبل ', '')}</span>}
    </div>
  );
}
